import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react';
import MentionAutocomplete from './MentionAutocomplete';
import usePresignedFileUpload, {
  isImageFile,
} from '../../lib/use_presigned_file_upload';

const MAX_ROWS = 10;
const MAX_ATTACHMENTS = 10;
const MAX_FILE_SIZE = 20 * 1024 * 1024;

// 画像＋一般文書。実行ファイルは accept でも弾きにくいため size/type を JS でも検証する。
const ACCEPT_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/*',
  'application/pdf',
  '.pdf',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.ppt',
  '.pptx',
  '.zip',
  'text/plain',
  'text/csv',
  '.txt',
  '.csv',
].join(',');

const ALLOWED_MIME_PREFIXES = ['image/'];
const ALLOWED_MIME_EXACT = new Set([
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/zip',
  'application/x-zip-compressed',
  'text/plain',
  'text/csv',
]);
const ALLOWED_EXTENSIONS = new Set([
  'jpg',
  'jpeg',
  'png',
  'gif',
  'webp',
  'pdf',
  'doc',
  'docx',
  'xls',
  'xlsx',
  'ppt',
  'pptx',
  'zip',
  'txt',
  'csv',
]);

const isAllowedFile = (file) => {
  if (!file) return false;
  const type = (file.type || '').toLowerCase();
  if (ALLOWED_MIME_PREFIXES.some((p) => type.startsWith(p))) return true;
  if (ALLOWED_MIME_EXACT.has(type)) return true;
  const ext = (file.name || '').split('.').pop()?.toLowerCase();
  return ALLOWED_EXTENSIONS.has(ext);
};

const autoResize = (el) => {
  if (!el) return;
  el.style.height = 'auto';
  const cs = window.getComputedStyle(el);
  const lineHeight = parseFloat(cs.lineHeight) || 20;
  const paddingV =
    (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0);
  const borderV =
    (parseFloat(cs.borderTopWidth) || 0) +
    (parseFloat(cs.borderBottomWidth) || 0);
  const maxHeight = lineHeight * MAX_ROWS + paddingV + borderV;
  el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`;
  el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden';
};

const nextLocalId = () =>
  `att_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

// 入力欄。最大10ファイル（画像＋文書）を R2 ダイレクトアップロードして cache_data を保持。
// アップロード中は送信不可。削除で AbortController.abort() して中断する。
// 親（MessagePane）からドラッグ&ドロップされたファイルを addFiles(ref) で受け取れる。
function MessageComposer({ circleId, channel, onSend, placeholder }, ref) {
  const [body, setBody] = useState('');
  const [attachments, setAttachments] = useState([]); // { localId, file, cacheData, status, previewUrl, isImage, controller }
  const [mentionUsers, setMentionUsers] = useState([]);
  const [mentionChannel, setMentionChannel] = useState(false);
  const [mentionEveryone, setMentionEveryone] = useState(false);
  const [mentionQuery, setMentionQuery] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const debounceRef = useRef(null);
  const { upload } = usePresignedFileUpload();

  const isUploading = attachments.some((a) => a.status === 'uploading');
  const readyCacheData = attachments
    .filter((a) => a.status === 'done' && a.cacheData)
    .map((a) => a.cacheData);

  const groupOptions = [];
  if (mentionQuery !== null) {
    const q = mentionQuery.toLowerCase();
    if (!channel.isGeneral && 'channel'.startsWith(q)) {
      groupOptions.push({
        key: 'channel',
        isGroup: true,
        label: '@channel',
        description: 'チャンネルのメンバー全員に通知',
      });
    }
    if (channel.isGeneral && 'everyone'.startsWith(q)) {
      groupOptions.push({
        key: 'everyone',
        isGroup: true,
        label: '@everyone',
        description: 'サークル全員に通知',
      });
    }
  }

  const detectMention = useCallback((value, caret) => {
    const before = value.slice(0, caret);
    const match = before.match(/(^|[\s\n])@([^\s@]*)$/);
    return match ? match[2] : null;
  }, []);

  useEffect(() => {
    if (mentionQuery === null) {
      setCandidates([]);
      return undefined;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(
          `/api/message/mention_candidates?circle_id=${circleId}&q=${encodeURIComponent(mentionQuery)}`,
          {
            credentials: 'same-origin',
            headers: { Accept: 'application/json' },
          },
        );
        if (res.ok) {
          const data = await res.json();
          setCandidates(data.candidates || []);
          setActiveIndex(0);
        }
      } catch (e) {
        setCandidates([]);
      }
    }, 200);
    return () => clearTimeout(debounceRef.current);
  }, [mentionQuery, circleId]);

  useEffect(() => {
    autoResize(textareaRef.current);
  }, [body]);

  const attachmentsRef = useRef(attachments);
  attachmentsRef.current = attachments;

  // アンマウント時に進行中アップロードを中断し object URL を解放
  useEffect(
    () => () => {
      attachmentsRef.current.forEach((a) => {
        if (a.controller) a.controller.abort();
        if (a.previewUrl) URL.revokeObjectURL(a.previewUrl);
      });
    },
    [],
  );

  const handleChange = (e) => {
    const { value, selectionStart } = e.target;
    setBody(value);
    setMentionQuery(detectMention(value, selectionStart));
    autoResize(e.target);
  };

  const insertMention = (option) => {
    const el = textareaRef.current;
    const caret = el ? el.selectionStart : body.length;
    const before = body.slice(0, caret).replace(/@([^\s@]*)$/, '');
    const after = body.slice(caret);

    if (option.isGroup) {
      const token = `@${option.key} `;
      setBody(`${before}${token}${after}`);
      if (option.key === 'channel') setMentionChannel(true);
      if (option.key === 'everyone') setMentionEveryone(true);
    } else {
      const handle = String(option.tunagate_id || '').trim();
      const label =
        handle ||
        String(option.name || '')
          .replace(/\s+/g, '')
          .replace(/^@+/, '');
      const token = `@${label} `;
      setBody(`${before}${token}${after}`);
      setMentionUsers((prev) =>
        prev.some((u) => u.id === option.id)
          ? prev
          : [...prev, { id: option.id }],
      );
    }
    setMentionQuery(null);
    if (el) el.focus();
  };

  const popupOpen =
    mentionQuery !== null && groupOptions.length + candidates.length > 0;

  const handleKeyDown = (e) => {
    if (popupOpen) {
      const total = groupOptions.length + candidates.length;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((i) => (i + 1) % total);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((i) => (i - 1 + total) % total);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        const all = [...groupOptions, ...candidates];
        insertMention(all[activeIndex]);
        return;
      }
      if (e.key === 'Escape') {
        setMentionQuery(null);
        return;
      }
    }
    if (
      e.key === 'Enter' &&
      (e.shiftKey || e.metaKey || e.ctrlKey) &&
      !e.nativeEvent.isComposing
    ) {
      e.preventDefault();
      submit();
    }
  };

  const startUpload = async (file) => {
    const localId = nextLocalId();
    const isImage = isImageFile(file);
    const previewUrl = isImage ? URL.createObjectURL(file) : null;
    const controller = new AbortController();

    setAttachments((prev) => [
      ...prev,
      {
        localId,
        file,
        cacheData: null,
        status: 'uploading',
        previewUrl,
        isImage,
        controller,
        fileName: file.name,
        fileSize: file.size,
      },
    ]);

    try {
      const cacheData = await upload(file, { signal: controller.signal });
      setAttachments((prev) =>
        prev.map((a) =>
          a.localId === localId
            ? { ...a, cacheData, status: 'done', controller: null }
            : a,
        ),
      );
    } catch (error) {
      const canceled =
        error?.code === 'ERR_CANCELED' ||
        error?.name === 'CanceledError' ||
        error?.name === 'AbortError';
      if (canceled) return;
      setAttachments((prev) =>
        prev.map((a) =>
          a.localId === localId
            ? { ...a, status: 'failed', controller: null }
            : a,
        ),
      );
    }
  };

  // ファイルピッカー・ドラッグ&ドロップ共通の添付キュー投入。
  // 残数上限・サイズ超過・許可外は静かに無視する。
  const enqueueFiles = (fileList) => {
    const picked = Array.from(fileList || []);
    if (!picked.length) return;

    const remaining = MAX_ATTACHMENTS - attachments.length;
    if (remaining <= 0) return;

    const accepted = picked.slice(0, remaining).filter((f) => {
      if (f.size > MAX_FILE_SIZE) return false;
      return isAllowedFile(f);
    });
    accepted.forEach((f) => startUpload(f));
  };

  const handleFileChange = (e) => {
    enqueueFiles(e.target.files);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // 親（MessagePane）のドラッグ&ドロップからファイルを受け取る
  useImperativeHandle(ref, () => ({ addFiles: enqueueFiles }));

  const removeAttachment = (localId) => {
    setAttachments((prev) => {
      const target = prev.find((a) => a.localId === localId);
      if (target?.controller) target.controller.abort();
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((a) => a.localId !== localId);
    });
  };

  const clearAllAttachments = () => {
    setAttachments((prev) => {
      prev.forEach((a) => {
        if (a.controller) a.controller.abort();
        if (a.previewUrl) URL.revokeObjectURL(a.previewUrl);
      });
      return [];
    });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const submit = () => {
    const trimmed = body.trim();
    if (isUploading) return;
    if (!trimmed && readyCacheData.length === 0) return;

    onSend({
      body: trimmed,
      attachments: readyCacheData,
      attachmentPreviews: attachments
        .filter((a) => a.status === 'done')
        .map((a) => ({
          file_name: a.fileName,
          file_size: a.fileSize,
          is_image: a.isImage,
          preview_url: a.previewUrl,
        })),
      mentionUserIds: mentionUsers.map((u) => u.id),
      mentionChannel,
      mentionEveryone,
    });
    setBody('');
    clearAllAttachments();
    setMentionUsers([]);
    setMentionChannel(false);
    setMentionEveryone(false);
    setMentionQuery(null);
  };

  const canSend =
    !isUploading && (!!body.trim() || readyCacheData.length > 0);

  return (
    <div className="slackch-composer">
      {popupOpen && (
        <MentionAutocomplete
          groupOptions={groupOptions}
          candidates={candidates}
          activeIndex={activeIndex}
          onSelect={insertMention}
        />
      )}
      <div className="slackch-composer-box">
        {attachments.length > 0 && (
          <div className="slackch-composer-previews">
            {attachments.map((a) => (
              <div
                key={a.localId}
                className={`slackch-composer-preview ${
                  a.status === 'uploading' ? 'slackch-composer-preview--uploading' : ''
                } ${a.status === 'failed' ? 'slackch-composer-preview--failed' : ''}`}
              >
                {a.isImage && a.previewUrl ? (
                  <img src={a.previewUrl} alt={a.fileName || '添付画像'} />
                ) : (
                  <div className="slackch-composer-filechip" title={a.fileName}>
                    <span className="slackch-composer-filechip-icon">📎</span>
                    <span className="slackch-composer-filechip-name">
                      {a.fileName}
                    </span>
                  </div>
                )}
                {a.status === 'uploading' && (
                  <span className="slackch-composer-preview-spinner" aria-label="アップロード中">
                    …
                  </span>
                )}
                {a.status === 'failed' && (
                  <span className="slackch-composer-preview-error">失敗</span>
                )}
                <button
                  type="button"
                  onClick={() => removeAttachment(a.localId)}
                  aria-label="添付を削除"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}
        <textarea
          ref={textareaRef}
          className="slackch-composer-textarea"
          value={body}
          placeholder={placeholder}
          rows={2}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
        />
        <div className="slackch-composer-actions">
          <button
            type="button"
            className="slackch-composer-icon"
            title="メンション"
            onClick={() => {
              setBody((v) => `${v}@`);
              setMentionQuery('');
              if (textareaRef.current) textareaRef.current.focus();
            }}
          >
            ＠
          </button>
          <label
            className="slackch-composer-icon"
            title="ファイルを添付（最大10）"
          >
            📎
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPT_TYPES}
              multiple
              onChange={handleFileChange}
              hidden
              disabled={attachments.length >= MAX_ATTACHMENTS}
            />
          </label>
          <button
            type="button"
            className="slackch-composer-send"
            onClick={submit}
            disabled={!canSend}
          >
            {isUploading ? 'アップロード中…' : '送信'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default forwardRef(MessageComposer);
